/**
 * Shopify Blog を WP REST API 互換モデルで返すクエリ層
 *
 * 目的:
 *   stories-guides / columns 系セクションのデータ源を WordPress REST から
 *   Shopify Blog（story-guide / news-topics）へ差し替える。DOM・CSS・フィルタ UI は
 *   既存セクションのまま、window.WordPress.api と同じ引数・返却形を提供する。
 *
 * データ源:
 *   snippets/blog-data-script.liquid が各セクション内に出力する
 *   <script type="application/json" data-blog-handle="..."> ノード。
 *   250件超は Section Rendering API で残ページを取得して結合する
 *   （assets/events-metaobject-loader.js と同一パターン）。
 *   最初の描画は 1ページ目だけで先に行い、残ページは whenComplete() で待つ。
 *   総件数は結合が終わるまで確定しないため、件数表示はセクション側で伏せる。
 *
 *   転送量削減のため、JSON は既定値と一致するキーを省略している
 *   （type / categories / tags / featuredMediaUrl / externalUrl / highlighted）。
 *   記事 handle も持たず url 末尾から復元する。既定値の補完は normalize() に集約。
 *   ノードは件数を絞ったもの（data-partial）やカテゴリで絞ったもの
 *   （data-filtered）が混在しうるため、同一ブログのノードは全て結合して扱う。
 *
 * タグエンコード規約（Matrixify インポート時に付与済み）:
 *   cat:{slug} / tag:{slug} / highlighted
 *   ※ JSON 化の段階（blog-article-json.liquid）で categories / tags 配列に分解済み
 *   ※ 誘導カードの判定・誘導先はどちらも externalUrl（custom.wp_external_url）だけ。
 *     移行時に付けた type:link-card タグは読まない
 *
 * WP REST との挙動互換:
 *   - categories / tags は OR 条件（いずれか一致で表示）
 *   - 入力スラッグは sanitize_title 相当の正規化後にスラッグと比較
 *     （例: 'Mt.Fuji Re-Style Project / NEWS' → 'mt-fuji-re-style-project-news'）
 *   - 一覧カードの分類表示は日本語名（タクソノミー辞書 → 固定表 → 生スラッグ）。
 *     辞書は metaobject `blog_taxonomies`（slug=cat:… / tag:…、label=表示名）
 *   - 並び: date（JSON の date＝wp_published_at 優先）、同時刻は WP id（handle の wp-{id}）降順
 *
 * 内容タグの横断:
 *   表示条件はカテゴリ／タグで両 Blog 共通。記事を置く Blog は置き場にすぎない。
 *   storiesAndGuides / columns は、ハブにカテゴリ・内容タグ・絞り込みタグの
 *   いずれかがあれば相手 Blog も混ぜる。全部空なら自 Blog のみ
 *   （未フィルタの全件横断を防ぐ）。
 *   絞り込みタグ（sharedTagSlugs）があるときは、それに一致する内容タグも必須。
 *   同タイトルが残っている場合の正本は news-topics（今回のデータ寄せ方針）。
 */
(() => {
  if (window.SalomonBlog && window.SalomonBlog._ready) return;

  // --- タクソノミーマップ（WP name 順のまま。フィルタ UI の並びに使用） ---
  const SG_CATEGORIES = [
    { name: "Multi-Sport", slug: "multi-sport" },
    { name: "クロスカントリースキー", slug: "cross-country-skiing" },
    { name: "スキー", slug: "alpine-skiing" },
    { name: "スノーボード", slug: "snowboarding" },
    { name: "スポーツスタイル", slug: "sportstyle" },
    { name: "トレイルランニング", slug: "trail-running" },
    { name: "ハイキング", slug: "hiking" },
    { name: "ランニング", slug: "road-running" }
  ];
  const SG_TAGS = [
    { name: "event", slug: "event-2" },
    { name: "イベント", slug: "event" },
    { name: "ギアガイド", slug: "gearguide" },
    { name: "ケアとリペア", slug: "care" },
    { name: "トレイルランを始める", slug: "begin" },
    { name: "ヒントとスキル", slug: "hint" },
    { name: "ブランドの舞台裏", slug: "brand" },
    { name: "挑戦者", slug: "challenger" }
  ];
  const COL_CATEGORIES = [
    { name: "News", slug: "news" },
    { name: "Race", slug: "race" },
    { name: "Special", slug: "secret" },
    { name: "Topics", slug: "topics" }
  ];
  const COL_TAGS = [
    { name: "Be Creative / Be Active", slug: "be-creative-be-active" },
    { name: "Mt.Fuji Re-Style Project / NEWS", slug: "mt-fuji-re-style-project-news" },
    { name: "Mt.Fuji Re-Style Project / SPOT", slug: "mt-fuji-re-style-project-spot" },
    { name: "Mt.Fuji Re-Style Project / STORY", slug: "mt-fuji-re-style-project-story" },
    { name: "race", slug: "race" },
    { name: "Salomon", slug: "salomon" },
    { name: "SKI", slug: "ski" },
    { name: "Snowboard", slug: "snowboard" },
    { name: "Special Interview", slug: "special-inerview" },
    { name: "STAFF VOICE", slug: "staff-voice" },
    { name: "Winter", slug: "winter" },
    { name: "ウィンター", slug: "ウィンター" },
    { name: "キャンペーン", slug: "キャンペーン" },
    { name: "スキー", slug: "スキー" },
    { name: "スノーボード", slug: "スノーボード" },
    { name: "ヒマラヤ", slug: "ヒマラヤ" },
    { name: "岐阜", slug: "岐阜" },
    { name: "挑戦者", slug: "challenger" },
    { name: "新店情報", slug: "新店情報" },
    { name: "最新情報", slug: "news" }
  ];

  const slug2name = terms => {
    const map = {};
    for (const { name, slug } of terms) {
      if (!(slug in map)) map[slug] = name;
    }
    return map;
  };

  /**
   * metaobject 辞書（cat:slug / tag:slug → label）。空なら固定表のみ。
   * ノードは blog-taxonomy-data.liquid が出力する。
   */
  const readTaxonomyDict = () => {
    const nodes = document.querySelectorAll("script[data-blog-taxonomy-dict]");
    for (const node of nodes) {
      try {
        const data = JSON.parse(node.textContent || "{}");
        if (data && typeof data === "object" && !Array.isArray(data) && Object.keys(data).length > 0) {
          return data;
        }
      } catch (e) {
        /* 次のノードへ */
      }
    }
    return {};
  };

  const TAXONOMY_DICT = readTaxonomyDict();
  const hasTaxonomyDict = Object.keys(TAXONOMY_DICT).length > 0;

  const overlayDict = (categories, tags) => {
    const nextCategories = { ...categories };
    const nextTags = { ...tags };
    for (const [key, label] of Object.entries(TAXONOMY_DICT)) {
      if (typeof label !== "string" || !label) continue;
      if (key.startsWith("cat:")) nextCategories[key.slice(4)] = label;
      else if (key.startsWith("tag:")) nextTags[key.slice(4)] = label;
    }
    return { categories: nextCategories, tags: nextTags };
  };

  const SG_MAPS = overlayDict(slug2name(SG_CATEGORIES), slug2name(SG_TAGS));
  const COL_MAPS = overlayDict(slug2name(COL_CATEGORIES), slug2name(COL_TAGS));
  const COMBINED_MAPS = {
    categories: { ...COL_MAPS.categories, ...SG_MAPS.categories },
    tags: { ...COL_MAPS.tags, ...SG_MAPS.tags }
  };

  const labelFor = (prefix, slug) => {
    const key = `${prefix}${slug}`;
    if (TAXONOMY_DICT[key]) return TAXONOMY_DICT[key];
    const maps = prefix === "cat:" ? COMBINED_MAPS.categories : COMBINED_MAPS.tags;
    return maps[slug] || slug;
  };

  const termsFromSlugs = (prefix, slugs) =>
    slugs.map(slug => ({ slug, name: labelFor(prefix, slug) }));

  const uniqueTermsFromBlogs = (handles, kind) => {
    const prefix = kind === "categories" ? "cat:" : "tag:";
    const seen = new Set();
    const terms = [];
    for (const handle of handles) {
      for (const article of readBlog(handle)) {
        for (const slug of article[kind] || []) {
          if (!slug || seen.has(slug)) continue;
          seen.add(slug);
          terms.push({ slug, name: labelFor(prefix, slug) });
        }
      }
    }
    terms.sort((a, b) => a.name.localeCompare(b.name, "ja"));
    return terms;
  };

  const listedTerms = (kind, fallback, handles, options = {}) => {
    const prefix = kind === "categories" ? "cat:" : "tag:";
    const configured = toArray(options.slugs);
    if (configured.length > 0) return termsFromSlugs(prefix, configured);
    if (hasTaxonomyDict) {
      const fromArticles = uniqueTermsFromBlogs(handles, kind);
      if (fromArticles.length > 0) return fromArticles;
    }
    return fallback.slice();
  };

  /**
   * WP sanitize_title 相当の簡易正規化（ASCII 部分のみ。日本語はそのまま）
   * WP は '.' を '-' に変換する（例: 'Mt.Fuji Re-Style Project / NEWS'
   * → 'mt-fuji-re-style-project-news'）
   */
  const sanitizeSlug = input => String(input ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s/｜|.]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");

  const matchesAny = (articleSlugs, querySlugs) => {
    if (!querySlugs || querySlugs.length === 0) return true;
    const wanted = new Set();
    for (const q of querySlugs) {
      const raw = String(q ?? "").trim();
      if (!raw) continue;
      wanted.add(raw);
      wanted.add(sanitizeSlug(raw));
    }
    if (wanted.size === 0) return true;
    return articleSlugs.some(slug => wanted.has(slug) || wanted.has(sanitizeSlug(slug)));
  };

  /**
   * Shopify の date フィルタは %z を "+0900"（コロンなし）で出力するが、
   * ES の Date Time String Format は "+09:00" を要求する。コロンなしは
   * 実装依存のフォールバック解析に落ちるため、必ず補ってから解釈する。
   * 解釈できない場合の 0 は「最古」として扱われる。
   */
  const parseDate = value => {
    const s = String(value ?? "").replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
    return Date.parse(s) || 0;
  };

  const toArray = value => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value.split(",").map(s => s.trim()).filter(s => s.length > 0);
    }
    return value == null ? [] : [value];
  };

  /**
   * Shopify CDN の画像 URL から width 違いの srcset を組み立てる。
   *
   * JSON には基準となる 1 本（536×330＝カード実寸 268×165 の 2倍）しか持たせない。
   * 幅は CDN のクエリを差し替えるだけで作れるので、解像度の出し分けのために
   * 記事ごとの URL を増やして HTML を太らせる必要はない。
   * height / crop が付いているときは幅に比例して height も差し替え、切れ方を保つ。
   * （events は JSON に 400/800 の 2 本を持たせているが、ブログは記事数が
   *   桁違いに多く、1 本増えるだけでペイロードが跳ねるためこの方式にした）
   *
   * @param {string} url 基準の画像 URL
   * @param {number[]} widths 生成する幅
   * @returns {string} srcset 文字列。組み立てられない URL では空文字
   */
  function srcsetFor(url, widths) {
    if (!url) return "";
    let parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (e) {
      return "";
    }
    // width クエリで解像度を切り替えられるのは Shopify CDN の画像のみ
    if (!parsed.searchParams.has("width")) return "";

    const baseWidth = Number(parsed.searchParams.get("width"));
    const baseHeight = Number(parsed.searchParams.get("height"));
    const scaleHeight = Number.isFinite(baseWidth) && baseWidth > 0 && Number.isFinite(baseHeight) && baseHeight > 0;

    return widths
      .map(width => {
        const variant = new URL(parsed.href);
        variant.searchParams.set("width", String(width));
        if (scaleHeight) {
          variant.searchParams.set("height", String(Math.round((width * baseHeight) / baseWidth)));
        }
        return `${variant.href} ${width}w`;
      })
      .join(", ");
  }

  /** url 末尾（= Shopify の article handle）から旧 WP 投稿 ID を取り出す */
  const wpIdOf = article => {
    const m = /\/wp-(\d+)(?:[?#]|$)/.exec(article.url || "");
    return m ? Number.parseInt(m[1], 10) : 0;
  };

  /** JSON で省略された既定値を補う（blog-article-json.liquid と対で維持すること） */
  const normalize = article => ({
    title: article.title || "",
    url: article.url || "",
    date: article.date || "",
    type: article.type || "post",
    externalUrl: article.externalUrl || "",
    categories: Array.isArray(article.categories) ? article.categories : [],
    tags: Array.isArray(article.tags) ? article.tags : [],
    featuredMediaUrl: article.featuredMediaUrl || "",
    highlighted: article.highlighted === true,
    youtubeId: article.youtubeId || "",
    youtubeDuration: article.youtubeDuration || ""
  });

  // ---------------------------------------------------------------------------
  // データ読み込み（段階読み込み。events / athletes と同一方針）
  //
  //   〜250件  … 1ページ目の JSON だけで完結。追加通信なし
  //   251件〜  … 先頭250件で即描画したあと、残りを Section Rendering API で
  //              取得して結合し、確定した総件数で描画し直す
  //
  // 旧実装は全ページの取得を待ってから最初の描画をしていたため、251件を超えた
  // 時点で毎回ネットワーク往復ぶん一覧が空白になっていた。
  // ---------------------------------------------------------------------------
  const store = {}; // blogHandle -> entry

  const parseArticleNode = node => {
    try {
      const parsed = JSON.parse(node.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("[SalomonBlog] JSON のパースに失敗しました", { id: node.id, error: e });
      return [];
    }
  };

  /** url で重複排除しながら entry へ取り込む。追加できた件数を返す */
  function mergeInto(entry, list) {
    let added = 0;
    for (const raw of list) {
      const article = normalize(raw);
      if (entry.byUrl.has(article.url)) continue;
      entry.byUrl.set(article.url, article);
      added++;
    }
    if (added > 0) entry.articles = [...entry.byUrl.values()];
    return added;
  }

  /**
   * ブログ単位のデータ入れ物を作る（初回のみ同期でパース）。
   *
   * 1ページに複数セクションが並ぶと、全件ノード・件数を絞った部分ノード
   * （data-partial）・カテゴリで絞ったノード（data-filtered）が混在しうる。
   * どれか1つを選ぶ方式ではセクションによって必要な記事を取り逃すため、
   * 全ノードを結合する（各ノードは自分のセクションが必要とする分を必ず含むので、
   * 和集合は全セクションの必要分を満たす）。
   */
  function entryFor(blogHandle) {
    if (store[blogHandle]) return store[blogHandle];

    const selector = `script[type="application/json"][data-blog-handle="${blogHandle}"]`;
    const nodes = [...document.querySelectorAll(selector)];
    if (nodes.length === 0) {
      console.warn(`[SalomonBlog] data script が見つかりません: ${blogHandle}`);
    }

    const entry = { nodes, byUrl: new Map(), articles: [], waiting: [], loading: false, complete: false };
    for (const node of nodes) mergeInto(entry, parseArticleNode(node));

    // 追加ページを持つノードが無ければこの時点で全件そろっている
    entry.complete = !nodes.some(node => Number.parseInt(node.dataset.articlesPages || "1", 10) > 1);

    store[blogHandle] = entry;
    return entry;
  }

  /** 251件超のノードについて 2ページ目以降を取得する。追加できた件数を返す */
  async function fetchRemainingPages(entry) {
    const basePath = window.location.pathname;
    let added = 0;

    for (const node of entry.nodes) {
      const totalPages = Number.parseInt(node.dataset.articlesPages || "1", 10);
      const sectionId = node.dataset.sectionId || "";
      if (totalPages <= 1 || !sectionId) continue;

      const failedPages = [];
      for (let page = 2; page <= totalPages; page++) {
        try {
          const params = new URLSearchParams({ sections: sectionId, page: String(page) });
          const res = await fetch(`${basePath}?${params.toString()}`, { credentials: "same-origin" });
          if (!res.ok) {
            failedPages.push({ page, reason: `HTTP ${res.status}` });
            continue;
          }
          const json = await res.json();
          const html = json[sectionId] || json[`sections/${sectionId}`];
          if (!html) {
            failedPages.push({ page, reason: "レスポンスに section キーなし" });
            continue;
          }
          const doc = new DOMParser().parseFromString(html, "text/html");
          const pageNode = doc.getElementById(node.id);
          if (!pageNode?.textContent) {
            failedPages.push({ page, reason: `script#${node.id} が見つからない` });
            continue;
          }
          added += mergeInto(entry, parseArticleNode(pageNode));
        } catch (e) {
          failedPages.push({ page, reason: e?.message || String(e) });
        }
      }

      // 絞り込みノードの data-articles-total は絞り込み前の総数なので照合しない
      // （athletes-metaobject-loader が data-athletes-filtered を見るのと同じ規約）
      const filtered = node.dataset.filtered !== undefined;
      const expectedTotal = filtered ? 0 : Number.parseInt(node.dataset.articlesTotal || "0", 10);
      if (failedPages.length > 0 || (expectedTotal > 0 && entry.byUrl.size < expectedTotal)) {
        console.warn(
          "[SalomonBlog] 追加ページの取得に問題があります。取得済みの分だけ表示します。",
          { id: node.id, expectedTotal, loaded: entry.byUrl.size, failedPages }
        );
      }
    }

    return added;
  }

  /** 1ページ目（最大250件）を同期で読み出す。追加通信は待たない */
  function readBlog(blogHandle) {
    return entryFor(blogHandle).articles;
  }

  /**
   * 対象ブログが 1ページ目だけで揃っているか（追加通信が不要か）。
   * 250件以下（＝ほとんどのケース）は常に true。
   *
   * @param {string|string[]} blogHandles 対象ブログ
   * @returns {boolean}
   */
  function isComplete(blogHandles) {
    return toArray(blogHandles).every(blogHandle => entryFor(blogHandle).complete);
  }

  /**
   * 251件超のときだけ残りページを取得し、結合が終わるまで待つ。
   * 250件以下では即解決し、追加通信も発生しない。
   *
   * 総件数はここが解決するまで確定しない。セクションは結合後にもう一度
   * 描画し、件数の表示もそのタイミングまで伏せる（198→204 のような
   * 書き換えを見せないため）。取得に失敗したページがあっても、取れた分で
   * 確定させて解決する（一覧が出ないままになるのを避ける）。
   *
   * @param {string|string[]} blogHandles 対象ブログ
   * @returns {Promise<void>}
   */
  function whenComplete(blogHandles) {
    const pending = [];

    for (const blogHandle of toArray(blogHandles)) {
      const entry = entryFor(blogHandle);
      if (entry.complete) continue;

      pending.push(new Promise(resolve => entry.waiting.push(resolve)));
      if (entry.loading) continue;

      // 失敗しても必ず確定させる。待ち続けると件数が出ないままになる
      const settle = () => {
        entry.loading = false;
        entry.complete = true;
        for (const resolve of entry.waiting.splice(0)) resolve();
      };
      entry.loading = true;
      fetchRemainingPages(entry).then(settle, settle);
    }

    return Promise.all(pending).then(() => undefined);
  }

  // --- WP REST 互換クエリ ---
  function toWpModel(article, maps) {
    return {
      id: wpIdOf(article),
      type: article.type,
      title: article.title,
      excerpt: "",
      // 誘導先は externalUrl（= custom.wp_external_url）だけで決める。
      // タグ運用に依存させると、付け忘れた記事で誘導が効かなくなる
      link: article.externalUrl || article.url,
      categories: article.categories.map(slug => maps.categories[slug] ?? slug),
      tags: article.tags.map(slug => maps.tags[slug] ?? slug),
      // 画像なしは空文字。undefined を返すと呼び出し側で img.src="undefined" に
      // なり存在しない URL への 404 リクエストが発生するため必ず文字列で返す。
      featuredMediaUrl: article.featuredMediaUrl,
      highlighted: article.highlighted,
      youtubeMetadata: article.youtubeId
        ? {
            id: article.youtubeId,
            url: `https://www.youtube.com/watch?v=${article.youtubeId}`,
            title: article.title,
            duration: article.youtubeDuration || undefined
          }
        : undefined
    };
  }

  async function queryBlog(blogHandles, maps, options = {}) {
    const {
      contentType,
      categories,
      tags,
      page = 1,
      perPage = 10,
      order = "desc",
      orderBy = "date"
    } = options;

    // 読み出しは同期。251件超の追加ページは enhanceWhenReady 側で後追いする
    // （ここで待つと一覧が空のままネットワーク往復を待つことになる）
    const handles = toArray(blogHandles);
    const seen = new Set();
    const all = [];
    for (const handle of handles) {
      for (const article of readBlog(handle)) {
        if (article.url && seen.has(article.url)) continue;
        seen.add(article.url);
        all.push(article);
      }
    }
    const categorySlugs = toArray(categories);
    const tagSlugs = toArray(tags);

    let filtered = all.filter(article =>
      (!contentType || article.type === contentType)
      && matchesAny(article.categories, categorySlugs)
      && matchesAny(article.tags, tagSlugs)
    );

    // orderBy modified は Blog では date と同一扱い（WP でも実質同順だった）
    const dir = order === "asc" ? 1 : -1;
    filtered = filtered.slice().sort((a, b) => {
      const da = parseDate(a.date);
      const db = parseDate(b.date);
      if (da !== db) return (da - db) * dir;
      return (wpIdOf(a) - wpIdOf(b)) * dir;
    });

    const total = filtered.length;
    const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 1;
    const start = (page - 1) * perPage;
    const items = filtered.slice(start, start + perPage).map(a => toWpModel(a, maps));

    return {
      pageInfo: { page, perPage, total, totalPages },
      items
    };
  }

  const titleKey = article => (article.title || "").replace(/\s+/g, " ").trim().toLowerCase();

  /** 同タイトルが両 Blog に残っているときの正本。表示条件自体は cat/tag。今回は news-topics に寄せた */
  const CANONICAL_HANDLE = "news-topics";

  /**
   * 内容タグ一覧を slug 重複なしで連結する（先に渡した側の名称を優先）。
   * 横断表示するハブでは、相手 Blog にしかない slug も絞り込みタグに
   * 書けば UI に出せるようにするため。
   */
  const dedupeBySlug = lists => {
    const seen = new Set();
    const merged = [];
    for (const list of lists) {
      for (const term of list) {
        if (seen.has(term.slug)) continue;
        seen.add(term.slug);
        merged.push(term);
      }
    }
    return merged;
  };

  /**
   * 自 Blog を主にし、ハブにカテゴリ／タグ条件があれば相手 Blog も混ぜる。
   * 表示は両 Blog とも同じ cat/tag 絞り込み。sharedTagSlugs があるときだけ
   * 内容タグのゲートを追加する（未フィルタ全件横断の防止）。
   * 同タイトルが残っている場合の正本は news-topics（今回寄せたデータ）。
   */
  async function queryWithSharedTags(primaryHandle, otherHandle, primaryMaps, options = {}) {
    const sharedTagSlugs = toArray(options.sharedTagSlugs);
    const {
      contentType,
      categories,
      tags,
      page = 1,
      perPage = 10,
      order = "desc"
    } = options;

    const categorySlugs = toArray(categories);
    const tagSlugs = toArray(tags);
    const shouldMix = sharedTagSlugs.length > 0 || categorySlugs.length > 0 || tagSlugs.length > 0;
    if (!shouldMix) {
      return queryBlog(primaryHandle, primaryMaps, options);
    }

    const dir = order === "asc" ? 1 : -1;

    const primaryArticles = readBlog(primaryHandle).filter(article =>
      (!contentType || article.type === contentType)
      && matchesAny(article.categories, categorySlugs)
      && matchesAny(article.tags, tagSlugs)
    );

    const otherArticles = readBlog(otherHandle).filter(article =>
      (!contentType || article.type === contentType)
      && matchesAny(article.categories, categorySlugs)
      && matchesAny(article.tags, tagSlugs)
      && (sharedTagSlugs.length === 0 || matchesAny(article.tags, sharedTagSlugs))
    );

    // 同タイトルは後勝ち。CANONICAL_HANDLE を必ず後ろに置いて正本にする
    const ordered = primaryHandle === CANONICAL_HANDLE
      ? [otherArticles, primaryArticles]
      : [primaryArticles, otherArticles];
    const merged = new Map();
    for (const list of ordered) {
      for (const article of list) {
        merged.set(titleKey(article), article);
      }
    }

    const filtered = [...merged.values()].sort((a, b) => {
      const da = parseDate(a.date);
      const db = parseDate(b.date);
      if (da !== db) return (da - db) * dir;
      return (wpIdOf(a) - wpIdOf(b)) * dir;
    });

    const total = filtered.length;
    const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 1;
    const start = (page - 1) * perPage;
    const items = filtered.slice(start, start + perPage).map(article => toWpModel(article, COMBINED_MAPS));

    return {
      pageInfo: { page, perPage, total, totalPages },
      items
    };
  }

  window.SalomonBlog = {
    _ready: true,
    readBlog,
    isComplete,
    whenComplete,
    srcsetFor,
    api: {
      storyAndGuideCategories: async (options) => listedTerms(
        "categories",
        SG_CATEGORIES,
        ["story-guide"],
        options
      ),
      // includeShared: 絞り込みタグに news-topics 側だけの slug を書いたときも
      // フィルタ UI に出す（横断表示するハブ用）。既定は従来どおり自 Blog のみ
      storyAndGuideTags: async (options) => listedTerms(
        "tags",
        options?.includeShared ? dedupeBySlug([SG_TAGS, COL_TAGS]) : SG_TAGS,
        options?.includeShared ? ["story-guide", "news-topics"] : ["story-guide"],
        options
      ),
      columnCategories: async (options) => listedTerms(
        "categories",
        COL_CATEGORIES,
        ["news-topics"],
        options
      ),
      columnTags: async (options) => listedTerms(
        "tags",
        options?.includeShared ? dedupeBySlug([COL_TAGS, SG_TAGS]) : COL_TAGS,
        options?.includeShared ? ["news-topics", "story-guide"] : ["news-topics"],
        options
      ),
      storiesAndGuides: options => queryWithSharedTags(
        options?.sourceBlog || "story-guide",
        "news-topics",
        SG_MAPS,
        options
      ),
      columns: options => queryWithSharedTags(
        options?.sourceBlog || "news-topics",
        "story-guide",
        COL_MAPS,
        options
      )
    }
  };
})();
